/** Console (admin) API client. All requests carry the admin Bearer token. */
import { authHeaders } from "./auth";
import { getJson } from "./http";
import type { Turn, SessionState } from "@shared/types";

export const PAGE_SIZE = 25;
export function hasMore(rows: unknown[], limit: number): boolean { return rows.length === limit; }

function qs(params: Record<string, string | number | undefined>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") parts.push(`${k}=${encodeURIComponent(String(v))}`);
  }
  return parts.length ? `?${parts.join("&")}` : "";
}

export function eventsQuery(o: { eventType?: string; actor?: string; limit?: number; offset?: number }): string {
  return `/api/console/events${qs({ limit: o.limit ?? PAGE_SIZE, offset: o.offset ?? 0, eventType: o.eventType, actor: o.actor })}`;
}


export interface ConsoleUser {
  userId: string;
  username: string;
  tenant: string;
  /** Live count of non-deleted conversations (current snapshot, #228). */
  currentConversations: number;
  /** All-time conversations ever created (immutable history, #228). */
  historicalConversations: number;
  messages: number;
  thumbsDown: number;
  lastActiveAt: number | null;
}
export interface ConsoleTurn {
  role: string;
  text?: string;
  responseId?: string;
  rating?: "up" | "down";
  ratingReason?: string;
}
export interface ConsoleConversation {
  sessionId: string;
  userId: string | null;
  username: string | null;
  tenant: string | null;
  title: string;
  updatedAt: number;
  turnCount: number;
  handedOff: boolean;
  /** Derived lifecycle state (active | idle | deleted, #228). */
  state: SessionState;
  /** Soft-delete time, or null when live (#228). */
  deletedAt: number | null;
}
export interface ConsoleAnnotation {
  id: number;
  sessionId: string;
  kind: string;
  payload: unknown;
  responseId: string | null;
  userId: string | null;
  username: string | null;
  tenant: string | null;
  createdAt: number;
  status: string;
  note: string;
  linkedIssue: string;
}
/**
 * HISTORICAL conversation lifecycle counts over a window (#228), from the
 * immutable event log — created/deleted never shrink retroactively, unlike the
 * CURRENT snapshot counts on ConsoleUser / ConsoleConversation.
 */
export interface LifecycleCounts {
  range: string;
  /** Conversations created in the window (immutable history). */
  created: number;
  /** Conversations deleted in the window (immutable history). */
  deleted: number;
}
/**
 * CURRENT online presence (#228 Phase 2). Online = last_seen within
 * PRESENCE_ONLINE_MS, derived at read time so an aged-out tab silently reads as
 * offline. `onlineUserIds` drives the per-user live indicator in the users table.
 */
export interface PresenceGroup { tenant: string; sessions: number; users: number; }
export interface PresenceSnapshot {
  byTenant: PresenceGroup[];
  onlineUserIds: string[];
  onlineUsers: number;
  totalSessions: number;
}
export interface ConsoleEvent {
  id: number;
  eventType: string;
  actor: string | null;
  tenant: string | null;
  method: string | null;
  path: string | null;
  status: number | null;
  latencyMs: number | null;
  createdAt: number;
  data: Record<string, unknown> | null;
}

/** One product-feedback row as the admin console consumes it (#284, Task C4/C5).
 *  Mirrors the server `ProductFeedbackRow`; screenshot bytes are excluded from
 *  the list and fetched per-item via the screenshot endpoint. */
export interface ConsoleProductFeedback {
  id: number;
  created_at: string;
  category: string | null;
  title: string | null;
  description: string | null;
  has_screenshot: boolean;
  screenshot_mime: string | null;
  conversation_id: string | null;
  response_id: string | null;
  page: string | null;
  app_version: string | null;
  user_id: string | null;
  username: string | null;
  tenant: string | null;
  enterprise: string | null;
  github_issue_number: number | null;
  status: string;
  assignee: string | null;
}

export const consoleApi = {
  fetchUsers: (o: { tenant?: string; limit?: number; offset?: number } = {}): Promise<{ users: ConsoleUser[] }> =>
    getJson(`/api/console/users${qs({ tenant: o.tenant, limit: o.limit ?? PAGE_SIZE, offset: o.offset ?? 0 })}`),
  fetchUser: (userId: string): Promise<{ user: ConsoleUser | null; conversations: ConsoleConversation[]; annotations: ConsoleAnnotation[]; events: ConsoleEvent[] }> =>
    getJson(`/api/console/users/${encodeURIComponent(userId)}`),
  fetchConversations: (o: { limit?: number; offset?: number; userId?: string; tenant?: string } = {}): Promise<{ conversations: ConsoleConversation[] }> =>
    getJson(`/api/console/conversations${qs({ limit: o.limit ?? PAGE_SIZE, offset: o.offset ?? 0, userId: o.userId, tenant: o.tenant })}`),
  fetchConversation: (id: string): Promise<{ turns: Turn[] }> =>
    getJson(`/api/console/conversations/${encodeURIComponent(id)}`),
  fetchAnnotations: (o: { limit?: number; offset?: number; userId?: string; kind?: string; kinds?: string[]; status?: string } = {}): Promise<{ annotations: ConsoleAnnotation[] }> =>
    getJson(`/api/console/annotations${qs({ limit: o.limit ?? PAGE_SIZE, offset: o.offset ?? 0, userId: o.userId, kind: o.kind, kinds: o.kinds !== undefined && o.kinds.length > 0 ? o.kinds.join(",") : undefined, status: o.status })}`),
  patchTriage: async (id: number, patch: { status?: string; note?: string; linkedIssue?: string }): Promise<void> => {
    const res = await fetch(`/api/console/annotations/${id}/triage`, {
      method: "PATCH",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(patch)
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
  },
  fetchEvents: (o: { eventType?: string; actor?: string; limit?: number; offset?: number } = {}): Promise<{ events: ConsoleEvent[] }> =>
    getJson(eventsQuery(o)),
  /** HISTORICAL created/deleted conversation counts in a window (#228). */
  fetchLifecycle: (o: { range?: string; tenant?: string } = {}): Promise<LifecycleCounts> =>
    getJson(`/api/admin/metrics/lifecycle${qs({ range: o.range, tenant: o.tenant })}`),
  /** CURRENT online presence — sessions/users grouped by tenant (#228 Phase 2). */
  fetchPresence: (): Promise<PresenceSnapshot> =>
    getJson(`/api/console/presence`),
  /** Product feedback (#284, Task C4/C5): list (screenshot bytes excluded). */
  fetchProductFeedback: (o: { tenant?: string; status?: string } = {}): Promise<{ items: ConsoleProductFeedback[] }> =>
    getJson(`/api/admin/product-feedback${qs({ tenant: o.tenant, status: o.status })}`),
  /** Per-item screenshot blob URL (served by the C4 screenshot endpoint). */
  productFeedbackScreenshotUrl: (id: number): string => `/api/admin/product-feedback/${id}/screenshot`,
  /** Apply a triage action (fixed vocabulary) to one feedback row. */
  triageProductFeedback: async (id: number, action: string, assignee?: string): Promise<{ status: string }> => {
    const res = await fetch(`/api/admin/product-feedback/${id}/triage`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(assignee !== undefined ? { action, assignee } : { action })
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return (await res.json()) as { status: string };
  }
};
